import React, { useCallback, useMemo, useEffect } from 'react';
import { ReactFlow, Controls, useReactFlow } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { DetailDeviceNode } from './nodes/DetailDeviceNode';
import { DetailStubNode } from './nodes/DetailStubNode';
import { PanelDeviceNode } from './nodes/PanelDeviceNode';
import { ConnectionEdge } from './edges/ConnectionEdge';
import { SheetBackground } from './SheetBackground';
import { ElementZoomControl } from './ElementZoomControl';
import { useStore, getActivePage } from '@/state/store';
import { SHEET } from '@/lib/svgExport';
import { useI18n } from '@/i18n/I18nContext';
import {
  computeDetailModel,
  deviceDisplayName,
  portNameGlobal,
  portIndexGlobal,
  detailPortCenterY,
  detailRowHeight,
  pickEdgeSides,
  getLagMap,
  DLAYOUT,
} from '@/lib/detail';
import { computePanelLayout, computePanelSideHints, pickBothSidesSide } from '@/lib/panel';
import { resolvePortVlans } from '@/lib/vlanPorts';
import { deviceLabel } from '@/lib/deviceIcons';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Layers, List, LayoutGrid, Tags, ScrollText } from 'lucide-react';

const nodeTypes = { detailDevice: DetailDeviceNode, detailStub: DetailStubNode, panelDevice: PanelDeviceNode };
const edgeTypes = { connection: ConnectionEdge };

export const DetailCanvas = () => {
  const { t, language } = useI18n();
  const doc = useStore((s) => s.doc);
  const page = useStore(getActivePage);
  const selectedNodeId = useStore((s) => s.selectedNodeId);
  const selectNode = useStore((s) => s.selectNode);
  const selectEdge = useStore((s) => s.selectEdge);
  const selectedEdgeId = useStore((s) => s.selectedEdgeId);
  const setDetailShowStubs = useStore((s) => s.setDetailShowStubs);
  const setDetailViewMode = useStore((s) => s.setDetailViewMode);
  const updateDetailMemberPos = useStore((s) => s.updateDetailMemberPos);
  const addEdgeForDevices = useStore((s) => s.addEdgeForDevices);
  const snapEnabled = useStore((s) => s.snapEnabled);
  const showBadges = useStore((s) => s.showPortVlanBadges);
  const setShowBadges = useStore((s) => s.setShowPortVlanBadges);
  const showLegend = useStore((s) => s.showVlanLegend);
  const setShowLegend = useStore((s) => s.setShowVlanLegend);
  const { fitBounds } = useReactFlow();

  // Show the WHOLE sheet (A3 page incl. CAD frame) on page switch / mount.
  const fitSheet = useCallback(() => {
    const s = page?.contentScale || 1;
    fitBounds({ x: 0, y: 0, width: SHEET.w / s, height: SHEET.h / s }, { padding: 0.04, duration: 200 });
  }, [fitBounds, page?.contentScale]);

  useEffect(() => {
    if (!page?.id) return undefined;
    const raf = requestAnimationFrame(fitSheet);
    return () => cancelAnimationFrame(raf);
  }, [page?.id, fitSheet]);

  const model = useMemo(() => (page ? computeDetailModel(doc, page) : { devices: [], internalEdges: [], stubs: [] }), [doc, page]);
  const memberIds = useMemo(() => new Set(model.devices.map((d) => d.deviceId)), [model]);
  const showStubs = !!page?.showStubs;
  const viewMode = page?.viewMode === 'panel' ? 'panel' : 'list';
  const isPanel = viewMode === 'panel';

  const panelSideHints = useMemo(() => (isPanel ? computePanelSideHints(model) : {}), [isPanel, model]);

  const panelLayouts = useMemo(() => {
    const m = {};
    if (isPanel) model.devices.forEach((d) => (m[d.deviceId] = computePanelLayout(d.node, { sideHints: panelSideHints[d.deviceId] })));
    return m;
  }, [isPanel, model, panelSideHints]);

  const vlanColorOf = (node) => {
    const v = doc.vlans.find((x) => x.id === node?.data?.vlanRef);
    return v ? v.color : null;
  };

  const rfNodes = useMemo(() => {
    const out = model.devices.map((d) => ({
      id: d.deviceId,
      type: isPanel ? 'panelDevice' : 'detailDevice',
      position: d.position,
      selected: d.deviceId === selectedNodeId,
      data: isPanel
        ? { ...d.node.data, sideHints: panelSideHints[d.deviceId] }
        : {
            deviceType: d.node.data?.deviceType,
            label: d.node.data?.label || deviceLabel(d.node.data?.deviceType, language),
            ports: (() => {
              const resolved = resolvePortVlans(d.node, doc);
              return d.ports.map((p) => {
                const r = resolved[p.id] || {};
                return {
                  ...p,
                  _color: r.color || null,
                  _fromUntagged: !!r.fromUntagged,
                  _untaggedVlanId: r.untaggedVlan ? r.untaggedVlan.vlanId : null,
                  _untaggedColor: r.untaggedVlan ? r.untaggedVlan.color : null,
                  _tagged: (r.taggedVlans || []).map((v) => ({ vlanId: v.vlanId, color: v.color })),
                  _groupName: r.groupName || null,
                };
              });
            })(),
            vlanColor: vlanColorOf(d.node),
          },
    }));
    if (showStubs) {
      model.stubs.forEach((s) => {
        const dev = model.devices.find((d) => d.deviceId === s.deviceId);
        if (!dev) return;
        const stubData = {
          otherName: deviceDisplayName(doc, s.otherDeviceId, language),
          otherPort: portNameGlobal(doc, s.otherDeviceId, s.otherPortId),
        };
        if (isPanel) {
          const a = panelLayouts[s.deviceId]?.anchors?.[s.portId];
          if (!a) return;
          if (a.dual) {
            // Patch panel: dock the external stub BELOW the port (bottom side).
            const stubLen = 13;
            out.push({
              id: 'stub-' + s.id,
              type: 'detailStub',
              position: { x: dev.position.x + a.anchorX - 9, y: dev.position.y + a.bottomY + stubLen },
              draggable: false,
              selectable: false,
              connectable: false,
              data: { ...stubData, handlePos: 'top', vertical: true },
            });
            return;
          }
          const top = a.side === 'top';
          const stubLen = 13;
          const boxH = 120;
          out.push({
            id: 'stub-' + s.id,
            type: 'detailStub',
            position: {
              x: dev.position.x + a.x - 9,
              y: top ? dev.position.y + a.y - stubLen - boxH : dev.position.y + a.y + stubLen,
            },
            draggable: false,
            selectable: false,
            connectable: false,
            data: { ...stubData, handlePos: top ? 'bottom' : 'top', vertical: true },
          });
        } else {
          const idx = Math.max(0, portIndexGlobal(doc, s.deviceId, s.portId));
          const rowH = detailRowHeight(dev.ports.length);
          out.push({
            id: 'stub-' + s.id,
            type: 'detailStub',
            position: { x: dev.position.x + DLAYOUT.BOX_W + 80, y: dev.position.y + detailPortCenterY(idx, rowH) - 16 },
            draggable: false,
            selectable: false,
            connectable: false,
            data: { ...stubData, handlePos: 'left' },
          });
        }
      });
    }
    return out;
  }, [model, selectedNodeId, showStubs, doc, language, isPanel, panelLayouts, panelSideHints]);

  const rfEdges = useMemo(() => {
    const devById = {};
    model.devices.forEach((d) => (devById[d.deviceId] = d));
    const lagById = getLagMap(doc);
    // Group internal edges by unordered device pair so multiple cables sharing a
    // corridor each get their own lane (centerX/centerY stagger in ConnectionEdge).
    const pairKey = (a, b) => [a, b].sort().join('|');
    const groups = {};
    model.internalEdges.forEach((e) => {
      const k = pairKey(e.sourceDeviceId, e.targetDeviceId);
      (groups[k] = groups[k] || []).push(e.id);
    });
    const out = model.internalEdges.map((e) => {
      // In list mode, pick connection sides from box geometry so stacked devices
      // get a straight vertical cable; panel mode keeps its fixed port handles,
      // except patch panels (both-sides layout) which dock TOP/BOTTOM to the
      // nearest peer (by vertical position) to visualise a pass-through.
      let sourceHandle = e.sourcePortId;
      let targetHandle = e.targetPortId;
      if (!isPanel) {
        const sd = devById[e.sourceDeviceId];
        const td = devById[e.targetDeviceId];
        const [sSide, tSide] = sd && td ? pickEdgeSides(sd.position, td.position) : ['r', 'l'];
        sourceHandle = `${e.sourcePortId}__${sSide}`;
        targetHandle = `${e.targetPortId}__${tSide}`;
      } else {
        const sd = devById[e.sourceDeviceId];
        const td = devById[e.targetDeviceId];
        const sL = panelLayouts[e.sourceDeviceId];
        const tL = panelLayouts[e.targetDeviceId];
        const sAnchor = sL?.anchors?.[e.sourcePortId];
        const tAnchor = tL?.anchors?.[e.targetPortId];
        if (sAnchor?.dual && sd && td) {
          const peerCenterY = (td.position?.y || 0) + (tL?.height || 120) / 2;
          const side = pickBothSidesSide(sd.position, sAnchor, peerCenterY);
          sourceHandle = `${e.sourcePortId}__${side === 'bottom' ? 'b' : 't'}`;
        }
        if (tAnchor?.dual && sd && td) {
          const peerCenterY = (sd.position?.y || 0) + (sL?.height || 120) / 2;
          const side = pickBothSidesSide(td.position, tAnchor, peerCenterY);
          targetHandle = `${e.targetPortId}__${side === 'bottom' ? 'b' : 't'}`;
        }
      }
      const siblings = groups[pairKey(e.sourceDeviceId, e.targetDeviceId)] || [e.id];
      const lag = e.lagId ? lagById[e.lagId] : null;
      return {
        id: e.id,
        source: e.sourceDeviceId,
        target: e.targetDeviceId,
        sourceHandle,
        targetHandle,
        type: 'connection',
        selected: e.id === selectedEdgeId,
        data: {
          connType: e.connType,
          parallelIndex: siblings.indexOf(e.id),
          parallelCount: siblings.length,
          routeSpread: 'lane',
          lagName: lag ? lag.name : null,
          lagColor: lag ? lag.color : null,
        },
      };
    });
    if (showStubs) {
      model.stubs.forEach((s) => {
        const dual = isPanel && panelLayouts[s.deviceId]?.anchors?.[s.portId]?.dual;
        out.push({
          id: 'stube-' + s.id,
          source: s.deviceId,
          sourceHandle: isPanel ? (dual ? `${s.portId}__b` : s.portId) : `${s.portId}__r`,
          target: 'stub-' + s.id,
          targetHandle: 'in',
          type: 'connection',
          selected: s.id === selectedEdgeId,
          data: { connType: s.connType },
        });
      });
    }
    return out;
  }, [model, showStubs, isPanel, selectedEdgeId, doc, panelLayouts]);

  const onNodesChange = useCallback(
    (changes) => {
      if (!page) return;
      changes.forEach((c) => {
        if (c.type === 'position' && c.position && memberIds.has(c.id)) {
          updateDetailMemberPos(page.id, c.id, c.position);
        }
      });
    },
    [page, memberIds, updateDetailMemberPos]
  );

  // Allow drawing connections directly on a detail page. Handle ids encode the
  // port (panel: "<portId>" or patch-panel "<portId>__t|b"; list:
  // "<portId>__l|r"); the created edge is stored on the standard page that owns
  // the source device.
  const onConnect = useCallback(
    (conn) => {
      if (!conn.source || !conn.target || conn.source === conn.target) return;
      const parsePort = (h) => (h ? h.replace(/__(l|r|t|b)$/, '') : null);
      addEdgeForDevices({
        sourceDeviceId: conn.source,
        targetDeviceId: conn.target,
        sourcePortId: parsePort(conn.sourceHandle),
        targetPortId: parsePort(conn.targetHandle),
      });
    },
    [addEdgeForDevices]
  );

  if (!page) return null;

  return (
    <div className="flex h-full w-full flex-col" data-testid="detail-canvas">
      {/* Detail toolbar */}
      <div className="flex flex-wrap items-center gap-3 border-b border-border bg-[#FBFBFA] px-3 py-2">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Layers className="h-3.5 w-3.5" />
          <span className="font-semibold uppercase tracking-wide">{t('detail_plan')}</span>
          <span className="tabular-nums">· {t('detail_members_count', { n: model.devices.length })}</span>
        </div>

        <div className="ml-auto flex items-center gap-4">
          <ToggleGroup
            type="single"
            value={viewMode}
            onValueChange={(v) => v && setDetailViewMode(page.id, v)}
            className="rounded-md border border-border bg-white p-0.5"
          >
            <ToggleGroupItem value="list" className="h-7 gap-1.5 px-2.5 text-xs data-[state=on]:bg-[#0B5FFF]/10 data-[state=on]:text-[#0B5FFF]" data-testid="detail-view-list">
              <List className="h-3.5 w-3.5" /> {t('view_list')}
            </ToggleGroupItem>
            <ToggleGroupItem value="panel" className="h-7 gap-1.5 px-2.5 text-xs data-[state=on]:bg-[#0B5FFF]/10 data-[state=on]:text-[#0B5FFF]" data-testid="detail-view-panel">
              <LayoutGrid className="h-3.5 w-3.5" /> {t('view_panel')}
            </ToggleGroupItem>
          </ToggleGroup>

          <div className="flex items-center gap-2">
            <Switch
              id="detail-show-stubs"
              checked={showStubs}
              onCheckedChange={(v) => setDetailShowStubs(page.id, v)}
              data-testid="detail-show-stubs-switch"
            />
            <Label htmlFor="detail-show-stubs" className="cursor-pointer text-xs text-[#2A3138]">
              {t('detail_show_external')}
            </Label>
          </div>

          {viewMode === 'list' && (
            <div className="flex items-center gap-2">
              <Tags className="h-3.5 w-3.5 text-muted-foreground" />
              <Switch id="detail-show-badges" checked={showBadges} onCheckedChange={setShowBadges} data-testid="detail-show-badges-switch" />
              <Label htmlFor="detail-show-badges" className="cursor-pointer text-xs text-[#2A3138]">
                {t('show_vlan_badges')}
              </Label>
            </div>
          )}

          <div className="flex items-center gap-2">
            <ScrollText className="h-3.5 w-3.5 text-muted-foreground" />
            <Switch id="detail-show-legend" checked={showLegend} onCheckedChange={setShowLegend} data-testid="detail-show-legend-switch" />
            <Label htmlFor="detail-show-legend" className="cursor-pointer text-xs text-[#2A3138]">
              {t('vlan_legend')}
            </Label>
          </div>

          <div className="h-5 w-px bg-border" />

          <ElementZoomControl page={page} />
        </div>
      </div>

      {/* Canvas */}
      <div className="netdraw-canvas relative min-h-0 flex-1 bg-[#e9e9e4]">
        <SheetBackground />
        <ReactFlow
          nodes={rfNodes}
          edges={rfEdges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodesChange={onNodesChange}
          onConnect={onConnect}
          onNodeClick={(_, node) => memberIds.has(node.id) && selectNode(node.id)}
          onEdgeClick={(_, edge) => {
            // Stub edges encode the real edge id behind a "stube-" prefix.
            const realId = edge.id.startsWith('stube-') ? edge.id.slice('stube-'.length) : edge.id;
            selectEdge(realId);
          }}
          onPaneClick={() => selectNode(null)}
          nodesConnectable={true}
          connectionMode="loose"
          edgesFocusable={true}
          snapToGrid={snapEnabled}
          snapGrid={[8, 16]}
          minZoom={0.1}
          maxZoom={3}
          deleteKeyCode={null}
          onInit={() => requestAnimationFrame(fitSheet)}
          proOptions={{ hideAttribution: true }}
        >
          <Controls showInteractive={false} />
        </ReactFlow>

        {model.devices.length === 0 && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-8 text-center">
            <p className="max-w-md rounded-md bg-white/85 px-4 py-3 text-sm text-muted-foreground ring-1 ring-border" data-testid="detail-empty-hint">
              {t('detail_empty')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
